const PLAY_STORE_BASE =
  'https://play.google.com/store/apps/details?id=com.sssk.justfun';

export const CHALLENGE_SITE_ORIGIN = 'https://justfun-website.netlify.app';

/**
 * Public challenge URL for Facebook, WhatsApp, YouTube, etc.
 */
export const buildChallengeUrl = (challengeId: string, source?: string): string => {
  const path = `${CHALLENGE_SITE_ORIGIN}/challenge/${encodeURIComponent(challengeId)}`;
  if (!source) return path;
  return `${path}?src=${encodeURIComponent(source)}`;
};

/**
 * Play Store URL with Install Referrer payload (new users).
 * challenge_id must be inside referrer=, not a separate query param.
 */
export const buildChallengePlayStoreUrl = (
  challengeId: string,
  source: string = 'website',
  medium: string = 'challenge_landing',
): string => {
  const referrer = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: challengeId,
    challenge_id: challengeId,
  }).toString();
  return `${PLAY_STORE_BASE}&referrer=${encodeURIComponent(referrer)}`;
};

const ANDROID_PACKAGE = 'com.sssk.justfun';

/**
 * Android Intent URL — opens the app when installed; otherwise Play Store (browser_fallback_url).
 * Works better than raw https App Links from in-app browsers (Facebook, WhatsApp).
 */
export const buildChallengeAndroidIntentUrl = (
  challengeId: string,
  source: string = 'website',
): string => {
  const id = encodeURIComponent(challengeId);
  const query = `?src=${encodeURIComponent(source)}`;
  const httpsPath = `/challenge/${id}${query}`;
  const fallback = encodeURIComponent(buildChallengePlayStoreUrl(challengeId, source));
  return (
    `intent://justfun-website.netlify.app${httpsPath}#Intent;` +
    `scheme=https;package=${ANDROID_PACKAGE};` +
    `action=android.intent.action.VIEW;` +
    `category=android.intent.category.BROWSABLE;` +
    `S.browser_fallback_url=${fallback};end`
  );
};

/** Custom scheme — opens app without Digital Asset Links verification. */
export const buildChallengeCustomSchemeUrl = (
  challengeId: string,
  source: string = 'website',
): string => {
  const params = new URLSearchParams({ src: source });
  return `justfun://challenge/${encodeURIComponent(challengeId)}?${params.toString()}`;
};

// --- Firestore-backed challenges (/challenge/fs/{id}) ---

export const buildFirestoreChallengeUrl = (challengeId: string, source?: string): string => {
  const path = `${CHALLENGE_SITE_ORIGIN}/challenge/fs/${encodeURIComponent(challengeId)}`;
  if (!source) return path;
  return `${path}?src=${encodeURIComponent(source)}`;
};

export const buildFirestoreChallengePlayStoreUrl = (
  challengeId: string,
  source: string = 'website',
  medium: string = 'challenge_landing',
): string => {
  const referrer = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: challengeId,
    challenge_id: challengeId,
    challenge_store: 'firestore',
  }).toString();
  return `${PLAY_STORE_BASE}&referrer=${encodeURIComponent(referrer)}`;
};

export const buildFirestoreChallengeAndroidIntentUrl = (
  challengeId: string,
  source: string = 'website',
): string => {
  const id = encodeURIComponent(challengeId);
  const query = `?src=${encodeURIComponent(source)}`;
  const httpsPath = `/challenge/fs/${id}${query}`;
  const fallback = encodeURIComponent(buildFirestoreChallengePlayStoreUrl(challengeId, source));
  return (
    `intent://justfun-website.netlify.app${httpsPath}#Intent;` +
    `scheme=https;package=${ANDROID_PACKAGE};` +
    `action=android.intent.action.VIEW;` +
    `category=android.intent.category.BROWSABLE;` +
    `S.browser_fallback_url=${fallback};end`
  );
};

export const buildFirestoreChallengeCustomSchemeUrl = (
  challengeId: string,
  source: string = 'website',
): string => {
  const params = new URLSearchParams({ src: source });
  return `justfun://challenge/fs/${encodeURIComponent(challengeId)}?${params.toString()}`;
};
